import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, UserCog, Mail, KeyRound } from "lucide-react";
import { NIVEL_EDUCATIVO_911, labelNivel } from "@/lib/sepCatalogos";
import { userLabel } from "@/lib/format";
import { parseCURP, isValidCURP } from "@/lib/curp";

const ROLE_LABEL = {
  superadmin: "Superadministrador",
  jefe_sector: "Jefe de Sector",
  supervisor_zona: "Supervisor de Zona",
  director: "Director(a)",
  docente: "Docente",
};

// Which role each actor can create
const CAN_CREATE = {
  superadmin: ["superadmin", "jefe_sector", "supervisor_zona", "director", "docente"],
  jefe_sector: ["supervisor_zona"],
  supervisor_zona: ["director"],
  director: ["docente"],
  docente: [],
};

export default function Usuarios() {
  const { user } = useAuth();
  const canCreate = CAN_CREATE[user?.role] || [];
  const defaultRole = canCreate[0] || "docente";
  const readOnly = false;
  const hideActions = ["jefe_sector", "supervisor_zona"].includes(user?.role);
  const isDirector = user?.role === "director";
  // Cuando el actor sólo puede crear UN tipo de usuario, ocultamos el selector de Rol
  // y los campos avanzados: el alta pide únicamente Correo + Nombre + Apellidos.
  const singleRoleCreator = canCreate.length === 1;
  const showAdvanced = !singleRoleCreator;

  const [items, setItems] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [planteles, setPlanteles] = useState([]);
  const [filterRole, setFilterRole] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(initialForm(defaultRole));
  const [rfcManual, setRfcManual] = useState(false);

  function initialForm(role) {
    return {
      email: "", nombre: "", apellido_paterno: "", apellido_materno: "",
      role, curp: "", rfc: "", telefono: "",
      fecha_nacimiento: "", genero: "",
      sector_ids: [], zona_ids: [], plantel_ids: [],
      nivel_educativo_911: "", nivel_educativo_otros: "",
    };
  }

  const load = async () => {
    try {
      const [u, s, z, p] = await Promise.all([
        api.get("/usuarios"),
        (user?.role === "superadmin" || user?.role === "jefe_sector") ? api.get("/sectores").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        (["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role)) ? api.get("/zonas").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get("/planteles").catch(() => ({ data: [] })),
      ]);
      setItems(u.data); setSectores(s.data); setZonas(z.data); setPlanteles(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filterRole === "__all__") return items;
    return items.filter((u) => u.role === filterRole);
  }, [items, filterRole]);

  const startCreate = () => {
    setEditing(null);
    const base = initialForm(defaultRole);
    // Director tiene un solo plantel: se autoasigna, no se pregunta.
    if (isDirector && (user?.plantel_ids?.length || 0) > 0) {
      base.plantel_ids = [...user.plantel_ids];
    }
    setForm(base);
    setRfcManual(false);
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, nombre: (u.nombre || "").toUpperCase(), apellido_paterno: (u.apellido_paterno || "").toUpperCase(),
      apellido_materno: (u.apellido_materno || "").toUpperCase(), role: u.role,
      curp: (u.curp || "").toUpperCase(), rfc: (u.rfc || "").toUpperCase(), telefono: u.telefono || "",
      fecha_nacimiento: u.fecha_nacimiento || "", genero: u.genero || "",
      sector_ids: u.sector_ids || [], zona_ids: u.zona_ids || [], plantel_ids: u.plantel_ids || [],
      nivel_educativo_911: u.nivel_educativo_911 || "",
      nivel_educativo_otros: u.nivel_educativo_otros || "",
    });
    const rfc0 = (u.rfc || "").toUpperCase();
    const curp0 = (u.curp || "").toUpperCase();
    setRfcManual(!!rfc0 && (curp0.length < 10 || rfc0 !== curp0.slice(0, 10)));
    setOpen(true);
  };

  const [reassignConfirm, setReassignConfirm] = useState(null); // { conflicts: [{plantel, director}], payload }

  const doSave = async (payload) => {
    try {
      if (editing) {
        const { role, ...rest } = payload;
        await api.put(`/usuarios/${editing.id}`, rest);
        toast.success("Usuario actualizado");
      } else {
        const res = await api.post("/usuarios", payload);
        if (res.data.email_sent) toast.success(`Cuenta creada — correo enviado a ${form.email}`);
        else toast.warning(`Cuenta creada. Contraseña temporal: ${res.data.temp_password_preview || "(revise el correo)"}`);
        if (payload.nivel_educativo_911 && res.data.id) {
          await api.put(`/usuarios/${res.data.id}`, {
            nivel_educativo_911: payload.nivel_educativo_911,
            nivel_educativo_otros: payload.nivel_educativo_otros || "",
          });
        }
      }
      setOpen(false); setReassignConfirm(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.curp && !isValidCURP(form.curp)) {
      toast.error("La CURP debe tener 18 caracteres, con dígitos en las posiciones 5 a 10.");
      return;
    }
    const payload = { ...form };
    if (payload.nivel_educativo_911 !== "OTROS") payload.nivel_educativo_otros = "";
    if (!["docente", "director"].includes(payload.role)) {
      delete payload.nivel_educativo_911;
      delete payload.nivel_educativo_otros;
    }
    // Detecta reasignación: si el rol es director y algún plantel ya tiene otro director
    if (payload.role === "director" && (payload.plantel_ids || []).length > 0) {
      const editingId = editing?.id;
      const conflicts = [];
      for (const pid of payload.plantel_ids) {
        const p = planteles.find((x) => x.id === pid);
        // 1) Buscar por plantel.director_user_id (fuente de verdad primaria)
        const linkedId = p?.director_user_id;
        // 2) Fallback: buscar por plantel_ids de los directores cargados
        const otherDir =
          (linkedId && linkedId !== editingId && items.find((u) => u.id === linkedId)) ||
          items.find((u) => u.role === "director" && u.id !== editingId && (u.plantel_ids || []).includes(pid));
        if (linkedId && linkedId !== editingId) {
          const dirLabel = otherDir
            ? (otherDir.name || [otherDir.nombre, otherDir.apellido_paterno, otherDir.apellido_materno].filter(Boolean).join(" ").trim() || otherDir.email)
            : "Director actual";
          conflicts.push({
            plantel: p ? `${p.cct} — ${p.nombre}` : pid,
            director: dirLabel,
          });
        }
      }
      if (conflicts.length > 0) {
        setReassignConfirm({ conflicts, payload });
        return;
      }
    }
    await doSave(payload);
  };

  const remove = async () => {
    try { await api.delete(`/usuarios/${confirm.id}`); toast.success("Usuario eliminado"); setConfirm(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPwd = async (u) => {
    try {
      const { data } = await api.post(`/usuarios/${u.id}/reset-password`);
      if (data.email_sent) toast.success(`Nueva contraseña enviada a ${u.email}`);
      else toast.warning("Contraseña reseteada, revise el correo");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // helpers for multi-select assignment
  const toggleId = (field, id) => {
    setForm((f) => {
      const cur = new Set(f[field] || []);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, [field]: Array.from(cur) };
    });
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Administración</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Usuarios</h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-56" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los roles</SelectItem>
                {Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            {canCreate.length > 0 && (
              <Button data-testid="btn-add-usuario" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
                <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Nombre</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Nivel educativo</TableHead>
                <TableHead>Estado</TableHead>
                {!hideActions && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={hideActions ? 5 : 6} className="text-center py-10">
                  <UserCog className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <div className="text-sm text-[#545454]">Sin usuarios</div>
                </TableCell></TableRow>
              ) : filtered.map((u) => (
                <TableRow key={u.id} data-testid={`usuario-row-${u.id}`}>
                  <TableCell className="font-medium">{userLabel(u) || u.email}</TableCell>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell><span className="inline-flex px-2 py-0.5 rounded text-xs bg-[#235B4E]/10 text-[#235B4E]">{ROLE_LABEL[u.role] || u.role}</span></TableCell>
                  <TableCell className="text-xs">
                    {u.nivel_educativo_911 ? labelNivel(u.nivel_educativo_911) : <span className="text-[#9CA3AF]">—</span>}
                    {u.nivel_educativo_911 === "OTROS" && u.nivel_educativo_otros && (
                      <span className="text-[#6B7280] ml-1">({u.nivel_educativo_otros})</span>
                    )}
                  </TableCell>
                  <TableCell>{u.activo === false ? <span className="text-[#9F2241] text-xs">Desactivado</span> : <span className="text-[#235B4E] text-xs">Activo</span>}</TableCell>
                  {!hideActions && (
                    <TableCell className="text-right">
                      {readOnly ? (
                        <span className="text-[10px] text-[#9CA3AF] italic">Solo lectura</span>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => resetPwd(u)} title="Reenviar contraseña"><KeyRound className="w-4 h-4 text-[#BC955C]" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(u)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirm(u)}><Trash2 className="w-4 h-4 text-[#9F2241]" /></Button>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              {!editing && (
                <span className="flex items-center gap-1 text-[#235B4E] mt-1">
                  <Mail className="w-3.5 h-3.5" /> Se asignará la contraseña temporal <span className="font-mono font-semibold">test12345</span>. El envío de correo está desactivado.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Correo" required full>
              <Input data-testid="usu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} />
            </Field>
            {!singleRoleCreator && (
              <Field label="Rol" required>
                {editing ? (
                  <Input value={ROLE_LABEL[form.role]} disabled />
                ) : (
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="usu-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{canCreate.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            )}
            {singleRoleCreator && (
              <Field label="Cargo" required>
                <Input value={ROLE_LABEL[form.role]} disabled data-testid="usu-cargo-readonly" />
              </Field>
            )}
            <Field label="Nombre" required>
              <Input data-testid="usu-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} required />
            </Field>
            <Field label="Apellido paterno">
              <Input value={form.apellido_paterno} onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Apellido materno">
              <Input value={form.apellido_materno} onChange={(e) => setForm({ ...form, apellido_materno: e.target.value.toUpperCase() })} />
            </Field>
            {showAdvanced && (<>
            <Field label="Teléfono">
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </Field>
            <Field label="CURP">
              <Input
                data-testid="usu-curp"
                value={form.curp}
                maxLength={18}
                className="font-mono"
                onChange={(e) => {
                  const curp = e.target.value.toUpperCase();
                  const parsed = parseCURP(curp);
                  const rfcAutoPrefix = curp.length >= 10 ? curp.slice(0, 10) : "";
                  setForm((f) => ({
                    ...f,
                    curp,
                    fecha_nacimiento: parsed?.fecha_nacimiento || f.fecha_nacimiento,
                    genero: parsed?.genero || f.genero,
                    rfc: !rfcManual && rfcAutoPrefix ? rfcAutoPrefix : f.rfc,
                  }));
                }}
              />
              {form.curp && form.curp.length > 0 && !isValidCURP(form.curp) && (
                <p className="text-[11px] text-[#9F2241] mt-1" data-testid="usu-curp-error">
                  Formato de CURP inválido. Debe tener 18 caracteres (4 letras + 6 dígitos + H/M + 5 letras + 2 alfanuméricos).
                </p>
              )}
            </Field>
            <Field label="RFC">
              <Input
                data-testid="usu-rfc"
                value={form.rfc}
                maxLength={13}
                className="font-mono"
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setRfcManual(val.length > 0);
                  setForm({ ...form, rfc: val });
                }}
              />
            </Field>
            <Field label="Fecha de nacimiento">
              <Input
                type="date"
                data-testid="usu-fecha-nac"
                value={form.fecha_nacimiento || ""}
                onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                readOnly={!!parseCURP(form.curp)?.fecha_nacimiento}
                className={parseCURP(form.curp)?.fecha_nacimiento ? "bg-[#F3F4F6] cursor-not-allowed" : ""}
              />
            </Field>
            <Field label="Género">
              <Select
                key={`gen-${form.genero || "none"}`}
                value={form.genero || "__none__"}
                onValueChange={(v) => setForm({ ...form, genero: v === "__none__" ? "" : v })}
                disabled={!!parseCURP(form.curp)?.genero}
              >
                <SelectTrigger data-testid="usu-genero"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin especificar —</SelectItem>
                  <SelectItem value="H">Hombre</SelectItem>
                  <SelectItem value="M">Mujer</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            </>)}

            {/* Role-specific assignments */}
            {form.role === "jefe_sector" && (
              <Field label="Sectores asignados" full>
                <MultiChecklist items={sectores.map((s) => ({ id: s.id, label: `${s.numero} ${s.nombre || ""}` }))} selected={form.sector_ids} onToggle={(id) => toggleId("sector_ids", id)} testid="assign-sectores" />
              </Field>
            )}
            {form.role === "supervisor_zona" && (
              <Field label="Zonas asignadas" full>
                <MultiChecklist items={zonas.map((z) => ({ id: z.id, label: `${z.numero} ${z.nombre || ""} — ${z.sector?.numero || "s/sector"}` }))} selected={form.zona_ids} onToggle={(id) => toggleId("zona_ids", id)} testid="assign-zonas" />
              </Field>
            )}
            {(form.role === "director" || form.role === "docente") && (
              <Field label={form.role === "director" ? "Escuela a cargo" : "Planteles asignados"} full>
                <MultiChecklist items={planteles.map((p) => ({ id: p.id, label: `${p.cct} — ${p.nombre}` }))} selected={form.plantel_ids} onToggle={(id) => toggleId("plantel_ids", id)} testid="assign-planteles" />
                {planteles.length === 0 && (
                  <p className="text-[11px] text-[#9F2241] mt-1">No hay planteles disponibles en su alcance.</p>
                )}
              </Field>
            )}

            {(form.role === "director" || form.role === "docente") && showAdvanced && (
              <>
                <Field label="Nivel educativo (Formato 911)" full>
                  <Select
                    value={form.nivel_educativo_911 || "__none__"}
                    onValueChange={(v) => setForm({ ...form, nivel_educativo_911: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="usu-nivel-educativo"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      <SelectItem value="__none__">— Sin especificar —</SelectItem>
                      {NIVEL_EDUCATIVO_911.map((n) => (
                        <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {form.nivel_educativo_911 === "OTROS" && (
                  <Field label="Especifique" full>
                    <Input
                      data-testid="usu-nivel-otros"
                      value={form.nivel_educativo_otros}
                      onChange={(e) => setForm({ ...form, nivel_educativo_otros: e.target.value.toUpperCase() })}
                      placeholder="Detalle del nivel educativo"
                    />
                  </Field>
                )}
              </>
            )}

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-usuario" type="submit" className="bg-[#235B4E] hover:bg-[#1D4A40]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se cerrarán sus sesiones activas y se desvincularán todas sus asignaciones.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reassignConfirm} onOpenChange={(o) => !o && setReassignConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reemplazar al director actual?</AlertDialogTitle>
            <AlertDialogDescription>
              Las siguientes escuelas ya tienen un director asignado. Si continúa, se removerá al director anterior y quedará asignado el nuevo:
              <ul className="mt-3 space-y-1 text-[13px] text-[#1F2937]">
                {(reassignConfirm?.conflicts || []).map((c, i) => (
                  <li key={i} className="border-l-2 border-[#9F2241] pl-2">
                    <strong>{c.plantel}</strong> — actual: <em>{c.director}</em>
                  </li>
                ))}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-reassign">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-reassign"
              onClick={() => doSave(reassignConfirm.payload)}
              className="bg-[#9F2241] hover:bg-[#801C34]"
            >
              Sí, reemplazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}{required && <span className="text-[#9F2241] ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function MultiChecklist({ items, selected, onToggle, testid }) {
  if (!items.length) return <div className="text-xs text-[#9CA3AF] p-3 border rounded">No hay elementos disponibles todavía.</div>;
  const sel = new Set(selected || []);
  return (
    <div data-testid={testid} className="border rounded p-2 max-h-48 overflow-y-auto space-y-1">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-[#F9FAFB] rounded cursor-pointer">
          <input type="checkbox" checked={sel.has(it.id)} onChange={() => onToggle(it.id)} />
          <span>{it.label}</span>
        </label>
      ))}
    </div>
  );
}
